// ─── SVG-Export des Stammbaums ───────────────────────────────────────────────
//
// Serialisiert das gerenderte Baum-SVG zu einer eigenständigen .svg-Datei –
// gedacht zum großformatigen Ausdrucken. Der Baum wird dabei aus der aktuellen
// Zoom-/Pan-Ansicht gelöst und in Naturgröße (voller Umfang) exportiert.
//
// Funktioniert, weil die Knoten ihre Farben/Konturen als Präsentations-Attribute
// tragen (fill="…", stroke="…") und nicht über CSS-Klassen – ein reiner
// DOM-Klon behält damit sein Aussehen ohne externes Stylesheet.

const MARGIN = 40; // Rand um den Baum (px), fängt u.a. Schlagschatten am Rand ab.

/**
 * Baut aus dem Live-SVG (`svgEl`) und seiner Zoom-Gruppe (`gEl`) einen
 * eigenständigen SVG-String über den gesamten Baum.
 */
export function buildTreeSvgString(svgEl: SVGSVGElement, gEl: SVGGElement): string {
  // getBBox liefert die Maße der Gruppen-Inhalte im lokalen (un-transformierten)
  // Koordinatensystem – also den Baum in Naturgröße, unabhängig von Zoom/Pan.
  const bbox = gEl.getBBox();

  const minX = Math.floor(bbox.x - MARGIN);
  const minY = Math.floor(bbox.y - MARGIN);
  const width = Math.ceil(bbox.width + MARGIN * 2);
  const height = Math.ceil(bbox.height + MARGIN * 2);

  const clone = svgEl.cloneNode(true) as SVGSVGElement;

  // Zoom-/Pan-Transform der äußeren Gruppe entfernen → Baum bei Ursprung.
  const clonedG = clone.querySelector('g');
  if (clonedG) clonedG.removeAttribute('transform');

  // Profilbilder entfernen: sie referenzieren /uploads mit einem Auth-Token,
  // wären in einer eigenständigen Datei nicht ladbar und sollen nicht als
  // Token in die Datei geraten. Der Platzhalter-Kreis bleibt erhalten.
  clone.querySelectorAll('image').forEach((n) => n.remove());

  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  clone.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
  clone.setAttribute('width', String(width));
  clone.setAttribute('height', String(height));
  clone.setAttribute('viewBox', `${minX} ${minY} ${width} ${height}`);
  // Schriftfamilie explizit setzen, damit der Text auch ohne die App-Umgebung
  // sauber fällt (Inter → System-Sans als Fallback).
  clone.setAttribute('font-family', 'Inter, system-ui, -apple-system, sans-serif');

  // Weißer Hintergrund fürs Drucken (das SVG selbst ist sonst transparent).
  const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
  bg.setAttribute('x', String(minX));
  bg.setAttribute('y', String(minY));
  bg.setAttribute('width', String(width));
  bg.setAttribute('height', String(height));
  bg.setAttribute('fill', '#ffffff');
  clone.insertBefore(bg, clone.firstChild);

  const serialized = new XMLSerializer().serializeToString(clone);
  return `<?xml version="1.0" encoding="UTF-8"?>\n${serialized}`;
}

/** Serialisiert den Baum und stößt den Datei-Download an. */
export function downloadTreeSvg(svgEl: SVGSVGElement, gEl: SVGGElement, filename: string): void {
  const svgString = buildTreeSvgString(svgEl, gEl);
  const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}
